import { SearchIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  getOperatorsForFilter,
  hasDateRangeControl,
  hasTimezoneControl,
} from "./config/schema-utils";
import type { StructuredFilterGroupRequest, StructuredFilterRequest } from "./config";
import type { StructuredSearchSchema } from "./config/structured-search-schema";
import AutocompleteDropdown from "./AutocompleteDropdown";
import {
  CompactFilterChips,
  CompactPlainSearchChip,
  type CompactChipItem,
} from "./CompactFilterChips";
import { StructuredSearchProvider, useStructuredSearchStore } from "./stores";
import { PLAIN_SEARCH_CHIP_ID } from "./stores/structured-search/structured-search-store";
import FilterBuilderRow from "./FilterBuilderRow";
import FilterChip from "./FilterChip";
import FilterEditPanel from "./FilterEditPanel";
import { useStructuredSearchKeyboard } from "./FilterBuilderRow/hooks/useStructuredSearchKeyboard";
import { isStructuredSearchApplyShortcut } from "./FilterBuilderRow/value-utils";
import { SearchBarDateRangePicker } from "./SearchBarDateRangePicker";
import { TimezoneSelector } from "./TimezoneSelector";

import RefreshButton from "@/components/ui/refresh-button";
import { cn } from "@/lib/utils";

interface StructuredSearchbarProps {
  /** Domain config for this search instance. Prefer setting this explicitly for non-request surfaces. */
  config?: StructuredSearchSchema;
  className?: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  hasNewData?: boolean;
  title?: string;
  showHeader?: boolean;
  /** Tighter layout for drawers / popovers. */
  variant?: "default" | "compact";
  showDateRangePicker?: boolean;
  cancelBuilderOnBlur?: boolean;
  onExploreInTable?: () => void;
  onExploreInTablePreload?: () => void;
  onFilterRemove?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  minDate?: Date;
  maxDate?: Date;
  /** When set, the main filter builder input is attached to this ref (e.g. Request Logs drawer / quick focus). */
  rootInputRef?: RefObject<HTMLInputElement | null>;
  /** Rendered next to the date range picker in the header row (e.g. Analytics). */
  headerActions?: ReactNode;
}

export const StructuredSearchbar = observer(
  ({
    config,
    className,
    onRefresh,
    isRefreshing = false,
    hasNewData = false,
    title = "Filters",
    showHeader = true,
    variant = "default",
    showDateRangePicker = true,
    cancelBuilderOnBlur = false,
    onExploreInTable,
    onExploreInTablePreload,
    onFilterRemove,
    onFocus,
    onBlur,
    minDate,
    maxDate,
    rootInputRef,
    headerActions,
  }: StructuredSearchbarProps) => {
    const store = useStructuredSearchStore();
    useLayoutEffect(() => {
      if (config && store.schema.id !== config.id) {
        store.replaceSchema(config);
      }
    }, [config, store]);

    const isCompact = variant === "compact";
    const placeholder = isCompact ? store.schema.compactPlaceholder : store.schema.placeholder;
    const internalInputRef = useRef<HTMLInputElement>(null);
    const inputRef = rootInputRef ?? internalInputRef;
    const editInputRef = useRef<HTMLInputElement>(null);
    const chipsContentRef = useRef<HTMLDivElement>(null);
    const builderContentRef = useRef<HTMLDivElement>(null);
    const previousEditingFilterIdRef = useRef<string | null>(null);
    const [chipsHeight, setChipsHeight] = useState<number | null>(null);
    const [builderHeight, setBuilderHeight] = useState<number | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const getInputRef = useCallback(
      () => (store.editingFilterId ? editInputRef.current : inputRef.current),
      [store.editingFilterId, inputRef],
    );
    const { handleKeyDown } = useStructuredSearchKeyboard(getInputRef);

    const collapseCompactSearch = useCallback(() => {
      if (!isCompact) {
        return;
      }
      store.setDropdownOpen(false);
      onBlur?.();
      inputRef.current?.blur();
    }, [inputRef, isCompact, onBlur, store]);

    const hasPlainSearch = Boolean(store.q.trim());
    const showChips = store.hasFilters || hasPlainSearch;
    const justExitedEditMode =
      previousEditingFilterIdRef.current !== null && store.editingFilterId === null;
    const showBuilderRow =
      !store.editingFilterId &&
      !justExitedEditMode &&
      (!showChips || isExpanded || store.step !== "idle");
    const isCollapsedWithChips = showChips && !showBuilderRow && !store.editingFilterId;
    const isDateLikeValueStep = (() => {
      if (store.step !== "value" || !store.inProgress.type || !store.inProgress.operator) {
        return false;
      }

      const operatorDefinition = getOperatorsForFilter(store.schema, store.inProgress.field).find(
        operator => operator.operator === store.inProgress.operator,
      );

      return (
        operatorDefinition?.valueInput === "date" || operatorDefinition?.valueInput === "daterange"
      );
    })();
    const shouldShowRootAutocompleteDropdown =
      !store.editingFilterId &&
      !justExitedEditMode &&
      (isExpanded || showBuilderRow) &&
      !isDateLikeValueStep;
    const shouldShowEditAutocompleteDropdown =
      Boolean(store.editingFilterId) && !isDateLikeValueStep;
    const shouldCancelEmptyCreationOnBlur =
      !store.editingFilterId &&
      store.step === "field" &&
      !store.inputText.trim() &&
      !store.inProgress.field;
    const shouldCancelBuilderOnBlur =
      shouldCancelEmptyCreationOnBlur ||
      (cancelBuilderOnBlur &&
        !store.editingFilterId &&
        store.step !== "idle" &&
        !(store.step === "value" && store.inProgress.field));

    const handleSearchFocusCapture = useCallback(() => {
      onFocus?.();
    }, [onFocus]);

    const handleBlurCapture = useCallback(
      (event: React.FocusEvent<HTMLDivElement>) => {
        const nextTarget = event.relatedTarget as Node | null;
        const nextElement = nextTarget instanceof HTMLElement ? nextTarget : null;
        const isOverflowTarget = Boolean(
          nextElement?.closest(
            "[data-structured-search-overflow], [data-structured-search-overflow-trigger]",
          ),
        );
        if (nextTarget && event.currentTarget.contains(nextTarget)) {
          return;
        }
        if (isOverflowTarget) {
          return;
        }

        onBlur?.();

        if (isDateLikeValueStep) {
          return;
        }

        store.setSelectedFilterIdForRemoval(null);

        if (shouldCancelBuilderOnBlur) {
          store.cancelBuilder();
          setIsExpanded(false);
          return;
        }

        if (store.hasFilters && !store.editingFilterId && store.step === "idle") {
          setIsExpanded(false);
        }
      },
      [isDateLikeValueStep, onBlur, shouldCancelBuilderOnBlur, store],
    );

    const handleContainerClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (showBuilderRow) {
          return;
        }

        const target = event.target as HTMLElement | null;
        if (target?.closest("button, input, [role='button']")) {
          return;
        }

        setIsExpanded(true);
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      },
      [showBuilderRow, inputRef],
    );

    const handleRemovePlainSearch = useCallback(() => {
      store.setSearch("");
      onFilterRemove?.();
      if (isCompact) {
        collapseCompactSearch();
        return;
      }
      inputRef.current?.focus();
    }, [collapseCompactSearch, inputRef, isCompact, onFilterRemove, store]);

    const handleRemoveFilter = useCallback(
      (id: string) => {
        store.removeFilter(id);
        onFilterRemove?.();
        collapseCompactSearch();
      },
      [collapseCompactSearch, onFilterRemove, store],
    );

    const handleSearchKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        handleKeyDown(event);
        if (event.key === "Escape") {
          requestAnimationFrame(collapseCompactSearch);
        }
      },
      [collapseCompactSearch, handleKeyDown],
    );

    const handleApplyShortcutCapture = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!isStructuredSearchApplyShortcut(event) || store.step !== "value") {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        store.completeFilter();
      },
      [store],
    );

    const handleEditPlainSearch = useCallback(() => {
      const plainSearchValue = store.q.trim();
      if (!plainSearchValue) {
        return;
      }

      store.setSearch("");
      setIsExpanded(true);
      store.openFieldSuggest();
      store.setInputText(plainSearchValue);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }, [store, inputRef]);

    const chipItems: CompactChipItem[] = [
      ...(hasPlainSearch
        ? [
            {
              id: PLAIN_SEARCH_CHIP_ID,
              kind: "plain-search" as const,
              label: store.q,
            },
          ]
        : []),
      ...store.filters.map(filter => ({
        id: filter.id,
        kind: "filter" as const,
        filter,
      })),
    ];

    useEffect(() => {
      const chipsElement = chipsContentRef.current;
      if (!chipsElement) {
        return;
      }

      const updateChipsHeight = () => {
        setChipsHeight(chipsElement.getBoundingClientRect().height);
      };

      updateChipsHeight();

      const resizeObserver = new ResizeObserver(() => {
        updateChipsHeight();
      });
      resizeObserver.observe(chipsElement);

      return () => resizeObserver.disconnect();
    }, [showChips, store.filters.length, store.q]);

    useEffect(() => {
      const builderElement = builderContentRef.current;
      if (!builderElement || store.editingFilterId) {
        return;
      }

      const updateBuilderHeight = () => {
        setBuilderHeight(builderElement.getBoundingClientRect().height);
      };

      updateBuilderHeight();

      const resizeObserver = new ResizeObserver(() => {
        updateBuilderHeight();
      });
      resizeObserver.observe(builderElement);

      return () => resizeObserver.disconnect();
    }, [
      showBuilderRow,
      store.editingFilterId,
      store.step,
      store.inputText,
      store.multiValues.length,
    ]);

    useEffect(() => {
      const wasEditing = previousEditingFilterIdRef.current !== null;
      const isEditing = store.editingFilterId !== null;

      if (wasEditing && !isEditing && store.hasFilters) {
        setIsExpanded(false);
      }

      previousEditingFilterIdRef.current = store.editingFilterId;
    }, [store.editingFilterId, store.hasFilters]);

    let searchShellRadiusClasses: string;
    if (store.editingFilterId) {
      searchShellRadiusClasses = isCompact
        ? "rounded-b-none rounded-t-xl border-b-transparent bg-white shadow-md shadow-gray-950/10"
        : "rounded-b-none rounded-t-2xl border-b-transparent bg-white shadow-md shadow-gray-950/10";
    } else if (isCompact) {
      searchShellRadiusClasses = "rounded-xl";
    } else {
      searchShellRadiusClasses = "rounded-2xl";
    }

    let builderMarginTop = "0px";
    if (showChips && showBuilderRow) {
      builderMarginTop = isCompact ? "6px" : "8px";
    }
    let compactSearchIconOffset = "";
    if (showBuilderRow && !showChips) {
      compactSearchIconOffset = "translate-y-[7px]";
    } else if (showBuilderRow) {
      compactSearchIconOffset = "translate-y-1";
    }
    const dateRangeEnabled = showDateRangePicker && hasDateRangeControl(store.schema);
    const timezoneEnabled = hasTimezoneControl(store.schema);
    const showDateRangeRow = dateRangeEnabled && showHeader && !isCompact;

    return (
      <div className={cn("flex flex-col", isCompact ? "gap-1.5" : "gap-2")}>
        {showDateRangeRow && (
          <div
            className={cn(
              "flex items-center",
              isCompact ? "justify-end py-0.5" : "justify-between",
            )}
          >
            {showHeader && !isCompact ? <h2 className="text-xl">{title}</h2> : null}
            <div className="flex items-center gap-2">
              {headerActions}
              {timezoneEnabled ? (
                <div className="flex shrink-0 items-center">
                  <TimezoneSelector triggerClassName="rounded-r-none" />
                  <SearchBarDateRangePicker
                    dateRange={store.dateRange}
                    minDate={minDate}
                    maxDate={maxDate}
                    className="-ml-px w-auto"
                    triggerClassName="h-10 rounded-l-none focus-visible:relative focus-visible:z-10"
                    onDateRangeChange={range => store.setDateRange(range)}
                  />
                </div>
              ) : (
                <SearchBarDateRangePicker
                  dateRange={store.dateRange}
                  minDate={minDate}
                  maxDate={maxDate}
                  onDateRangeChange={range => store.setDateRange(range)}
                />
              )}
            </div>
          </div>
        )}
        <div
          className={cn("group relative", className)}
          onBlurCapture={handleBlurCapture}
          onFocusCapture={handleSearchFocusCapture}
          onKeyDownCapture={handleApplyShortcutCapture}
        >
          <div
            className={cn(
              "flex border border-gray-200/80 bg-[#f7f7f8] shadow-sm shadow-gray-950/5 transition-[border-radius,background-color,box-shadow] duration-200 ease-out group-focus-within:bg-white",
              isCompact ? "min-h-10 gap-2 px-3 py-2" : "min-h-14 gap-3 px-4 py-3",
              isCompact && !showBuilderRow ? "items-center" : "items-start",
              searchShellRadiusClasses,
              isCollapsedWithChips &&
                "cursor-pointer hover:border-gray-300 hover:bg-white hover:shadow-md",
            )}
            onKeyDown={handleSearchKeyDown}
            onClick={handleContainerClick}
          >
            <SearchIcon
              className={cn(
                "shrink-0 text-gray-400 transition-transform duration-200 ease-out",
                isCompact ? cn("h-3.5 w-3.5", compactSearchIconOffset) : "mt-1.5 h-4 w-4",
              )}
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <div
                className="overflow-hidden transition-[height] duration-200 ease-out"
                style={
                  showChips && chipsHeight !== null ? { height: `${chipsHeight}px` } : { height: 0 }
                }
              >
                <div ref={chipsContentRef}>
                  {showChips && (
                    <>
                      {isCompact ? (
                        <CompactFilterChips
                          items={chipItems}
                          selectedFilterIdForRemoval={store.selectedFilterIdForRemoval}
                          editingFilterId={store.editingFilterId}
                          isFilterLocked={id => store.isFilterLocked(id)}
                          onEditPlainSearch={handleEditPlainSearch}
                          onRemovePlainSearch={handleRemovePlainSearch}
                          onEditFilter={id => store.startEditFilter(id)}
                          onRemoveFilter={handleRemoveFilter}
                        />
                      ) : (
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          {hasPlainSearch && (
                            <CompactPlainSearchChip
                              label={store.q}
                              onEdit={handleEditPlainSearch}
                              onRemove={handleRemovePlainSearch}
                              selectedForRemoval={
                                store.selectedFilterIdForRemoval === PLAIN_SEARCH_CHIP_ID
                              }
                            />
                          )}
                          {store.filters.map(f => (
                            <FilterChip
                              key={f.id}
                              filter={f}
                              onRemove={() => store.removeFilter(f.id)}
                              onEdit={() => store.startEditFilter(f.id)}
                              selectedForRemoval={store.selectedFilterIdForRemoval === f.id}
                              isEditing={store.editingFilterId === f.id}
                              isLocked={store.isFilterLocked(f.id)}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              {!store.editingFilterId && (
                <div
                  className={cn(
                    "overflow-hidden transition-[height,opacity,margin] duration-200 ease-out",
                    showBuilderRow
                      ? "visible opacity-100"
                      : "pointer-events-none invisible opacity-0",
                  )}
                  style={{
                    height: showBuilderRow ? `${builderHeight ?? 0}px` : "0px",
                    marginTop: builderMarginTop,
                  }}
                  aria-hidden={!showBuilderRow}
                >
                  <div ref={builderContentRef}>
                    <FilterBuilderRow
                      inputRef={inputRef}
                      placeholder={placeholder}
                      isEditMode={false}
                      showAutocompleteDropdown={false}
                      onStructuredKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
              )}
            </div>
            <div
              className={cn(
                "flex shrink-0 items-center gap-2 transition-transform duration-200 ease-out",
                isCompact ? cn(showBuilderRow && "translate-y-0.5") : "mt-[3px]",
              )}
            >
              {isCompact && dateRangeEnabled && (
                <SearchBarDateRangePicker
                  dateRange={store.dateRange}
                  minDate={minDate}
                  maxDate={maxDate}
                  variant="tinyIcon"
                  onDateRangeChange={range => store.setDateRange(range)}
                />
              )}
              {onRefresh && (
                <RefreshButton
                  onClick={onRefresh}
                  isRefreshing={isRefreshing}
                  size="xs"
                  hasNewData={hasNewData}
                />
              )}
            </div>
          </div>
          {shouldShowRootAutocompleteDropdown && (
            <div className="relative">
              <AutocompleteDropdown
                isEditorActive
                onExploreInTable={onExploreInTable}
                onExploreInTablePreload={onExploreInTablePreload}
              />
            </div>
          )}
          <FilterEditPanel
            editInputRef={editInputRef}
            onStructuredKeyDown={handleKeyDown}
            variant={isCompact ? "compact" : "default"}
          />
          {shouldShowEditAutocompleteDropdown && (
            <div className="relative">
              <AutocompleteDropdown
                isEditorActive
                onExploreInTable={onExploreInTable}
                onExploreInTablePreload={onExploreInTablePreload}
              />
            </div>
          )}
          {store.applyValidationMessage && (
            <div
              className={cn(
                "mt-2 rounded-xl border border-red-200 bg-red-50 text-red-700 shadow-sm",
                isCompact ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm",
              )}
              role="alert"
            >
              {store.applyValidationMessage}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export type { StructuredFilterGroupRequest, StructuredFilterRequest };
export { StructuredSearchProvider, useStructuredSearchStore };
export default StructuredSearchbar;
